/**
 * MessageList Component
 *
 * Displays a list of chat messages with proper spacing and grouping.
 *
 * Usage:
 *   <MessageList messages={messages} />
 */

import React from 'react';
import { MessageBubble } from './MessageBubble';
import type { Message } from './ChatInterface';

export interface MessageListProps {
  messages: Message[];
  conversationMedia?: Array<{ filename: string; url: string; type: string }>;
  conversationId?: string;
  onPublish?: () => void;
  onToggleMediaCanvas?: () => void;
  isMediaCanvasOpen?: boolean;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  conversationMedia = [],
  conversationId,
  onPublish,
  onToggleMediaCanvas,
  isMediaCanvasOpen
}) => {
  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p className="text-ink-3">
            Start a conversation to create your campaign
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {messages.map((message, index) => {
        // Check if this message is from the same sender as previous
        const previousMessage = index > 0 ? messages[index - 1] : null;
        const isGrouped = previousMessage?.role === message.role;

        return (
          <div
            key={message.id}
            className={isGrouped ? 'mt-2' : 'mt-6'}
          >
            <MessageBubble
              role={message.role}
              content={message.content}
              conversationMedia={conversationMedia}
              showAvatar={!isGrouped}
              timestamp={message.created_at}
              conversationId={conversationId}
              onPublish={onPublish}
              onToggleMediaCanvas={onToggleMediaCanvas}
              isMediaCanvasOpen={isMediaCanvasOpen}
            />
          </div>
        );
      })}
    </div>
  );
};

export default MessageList;
